import React from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import 'dayjs/locale/de';
import { GeneratedDate, holidayNameOf } from '../../../../../utils/recurrence';
import { useGetHolidayStatusQuery } from '../../../../../api/account/accountApiSlice';

interface SeriesDateListProps {
    /**
     * The dates of the server preview (`POST /admin/series/preview`). A date
     * the booking guard would refuse carries `hasConflict`; a taken one stays
     * selected and is created as a conflict unless removed. The browser no
     * longer guesses conflicts from the booking list, which missed holds,
     * divided rooms and cancelled-but-active rows. A holiday (M9, #3041) is
     * labelled: a public one arrives deselected and is booked only if ticked,
     * a school one is only marked.
     */
    dates: GeneratedDate[];
    onDatesChange: (dates: GeneratedDate[]) => void;
}

/** The label of a holiday date: a public one says whether it was put back. */
const holidayLabelKey = (kind: 'public' | 'school', included: boolean, windowPaused: boolean): string => {
    if (kind === 'school') {
        return windowPaused
            ? 'series_booking.date_review.holiday_school_paused'
            : 'series_booking.date_review.holiday_school';
    }
    return included ? 'series_booking.date_review.holiday_public_included' : 'series_booking.date_review.holiday_public';
};

const SeriesDateList: React.FC<SeriesDateListProps> = ({ dates, onDatesChange }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
    // Without a holiday region nothing is marked; say so rather than let an
    // unmarked list read as "no holidays in this range" (decision 2).
    const { data: holidayStatus } = useGetHolidayStatusQuery();
    const noHolidayRegion = holidayStatus !== undefined && holidayStatus.region === null;

    const includedCount = dates.filter((d) => d.included).length;
    const totalCount = dates.length;

    const toggleDate = (index: number) => {
        const updated = [...dates];
        updated[index] = { ...updated[index], included: !updated[index].included };
        onDatesChange(updated);
    };

    const removeDate = (index: number) => {
        const updated = [...dates];
        updated[index] = { ...updated[index], included: false };
        onDatesChange(updated);
    };

    return (
        <div className={'rounded-lg bg-white p-6 shadow-sm'}>
            <div className={'mb-4 flex items-center justify-between'}>
                <Typography variant={'h6'}>
                    {t('series_booking.date_review.title')}
                </Typography>
                <div className={'flex items-center gap-3'}>
                    <div className={'rounded-md bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700'}>
                        {t('series_booking.date_review.selected_count', { included: includedCount, count: totalCount })}
                    </div>
                </div>
            </div>

            {noHolidayRegion && dates.length > 0 && (
                <p className={'mb-3 text-sm text-foreground/60'}>
                    {t('series_booking.date_review.no_holiday_region')}
                </p>
            )}

            {/* Date List */}
            <div className={'max-h-[500px] overflow-y-auto'}>
                {dates.map((dateEntry, index) => {
                    const hasConflict = dateEntry.hasConflict ?? false;

                    return (
                        <div
                            key={dateEntry.date.toISOString()}
                            className={`flex items-center justify-between border-b border-gray-100 px-3 py-3 last:border-b-0 ${
                                !dateEntry.included ? 'bg-gray-50 opacity-50' : ''
                            } ${hasConflict && dateEntry.included ? 'bg-red-50' : ''}`}
                        >
                            <div className={'flex items-center gap-3'}>
                                <input
                                    type={'checkbox'}
                                    checked={dateEntry.included}
                                    onChange={() => toggleDate(index)}
                                    className={'h-4 w-4 rounded border-gray-300 text-primary'}
                                />
                                <span
                                    className={'text-sm font-medium'}
                                    title={
                                        hasConflict
                                            ? t('series_booking.date_review.conflict')
                                            : t('series_booking.date_review.available')
                                    }
                                >
                                    {hasConflict ? '⚠️' : '✅'}
                                </span>
                                <span className={'text-sm'}>
                                    {dateEntry.date.locale(lang).format(
                                        lang === 'de' ? 'dd, DD.MM.YYYY' : 'ddd, MM/DD/YYYY',
                                    )}
                                </span>
                                <span className={'text-sm text-gray-500'}>
                                    {dateEntry.startTime}–{dateEntry.endTime}
                                </span>
                                {dateEntry.holiday && (
                                    <span className={'text-xs text-foreground/60'}>
                                        {t(holidayLabelKey(
                                            dateEntry.holiday.kind,
                                            dateEntry.included,
                                            dateEntry.holiday.windowPaused === true,
                                        ), {
                                            name: holidayNameOf(dateEntry.holiday, lang),
                                        })}
                                    </span>
                                )}
                                {hasConflict && (
                                    <span className={'text-xs text-red-600'}>
                                        {dateEntry.included && dateEntry.canCreateAsConflict
                                            ? t('series_booking.date_review.created_as_conflict')
                                            : t('series_booking.date_review.conflict')}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => removeDate(index)}
                                className={'text-sm text-gray-400 hover:text-red-500'}
                                title={t('series_booking.date_review.remove_date')}
                            >
                                ✕
                            </button>
                        </div>
                    );
                })}
            </div>

            {dates.length === 0 && (
                <div className={'py-8 text-center text-sm text-gray-500'}>
                    {t('series_booking.date_review.no_dates')}
                </div>
            )}
        </div>
    );
};

export default SeriesDateList;
